import { Link, useNavigate, useParams } from 'react-router';
import { IconArrowLeft, IconLock, IconPhoto, IconShieldCheck } from '@tabler/icons-react';
import {
  Badge,
  Button,
  EmptyState,
  Metric,
  Panel,
  cn,
  formatDate,
} from 'escrowbuild-ui';

import {
  useFixtureVocabulary,
  usePublishedDocuments,
  usePublishedEvidence,
  usePublishedPublications,
  useT,
  type AppTranslationKey,
} from '@/i18n';
import {
  PROJECTS,
  participationsForInvestor,
  type DocumentRecord,
} from '@/data/domain';

/* =============================================================================
   Investor portal — one published update
   -----------------------------------------------------------------------------
   Provenance is the page. An investor is being asked to trust a photograph and a
   number they cannot verify themselves, so the approval is placed directly under
   the title rather than in a footer: who prepared it, who reviewed it, who
   approved it and on what day, all before the narrative begins.

   Reachable only by the investor it belongs to. A publication that is not
   published, or that belongs to a development outside this portfolio, resolves to
   the same refusal — the screen never distinguishes the two, because doing so
   would disclose that the other one exists.
   ============================================================================= */

/** The signed-in investor — see the note in `PortalHomePage`. */
const INVESTOR_ID = 'prado';

const FOCUS_RING_LINK = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus';

/** The word each document kind is shown as. The code itself is a wire value. */
const DOCUMENT_KIND: Record<DocumentRecord['type'], AppTranslationKey> = {
  invoice: 'status.documentType.invoice',
  receipt: 'status.documentType.receipt',
  contract: 'status.documentType.contract',
  permit: 'status.documentType.permit',
  quote: 'status.documentType.quote',
  report: 'status.documentType.report',
  inspection: 'status.documentType.inspection',
};

/*
  The month an update covers used to be read out of its PERIOD — `"Early
  September 2026"` → `"2026-09"` — by matching English month names inside the
  string. A period is prose a reader reads in their own language, so that match
  stops working the moment the page is Portuguese and the panel silently falls
  back to the whole record. The date the update was published names the same
  month for every update here, and a date is not a word.
*/

export function PortalUpdateDetailPage() {
  const { publicationId } = useParams<{ publicationId: string }>();
  const navigate = useNavigate();
  const t = useT();

  const projectIds = new Set(participationsForInvestor(INVESTOR_ID).map((entry) => entry.projectId));

  /*
     Everything this page names a record from, resolved for the active locale and
     resolved BEFORE the refusal below, because a hook cannot run after a
     conditional return. Read whole and narrowed to the update's development
     afterwards, because the development is not known until the update is.
  */
  const releases = usePublishedPublications();
  const photos = usePublishedEvidence();
  const files = usePublishedDocuments();
  const vocabulary = useFixtureVocabulary();

  const publication = releases.find(
    (entry) => entry.id === publicationId && projectIds.has(entry.projectId),
  );

  /** A phase's name, or the sentence this screen uses for one it does not hold. */
  const phaseLabel = (phaseId: string | null): string => {
    const fallback = t('portal.update.phaseFallback');
    if (!phaseId) return fallback;
    /* The resolver answers with the id itself when it has no word for one. */
    const name = vocabulary.phase(phaseId);
    return name === phaseId ? fallback : name;
  };

  if (!publication) {
    return (
      <section className="flex flex-col">
        <h1 className="sr-only">{t('portal.update.notAvailableHeading')}</h1>
        <EmptyState
          variant="page"
          icon={<IconLock size={32} aria-hidden="true" />}
          title={t('portal.update.refusedTitle')}
          description={t('portal.update.refusedBody')}
          action={
            <Button onClick={() => navigate('/portal/updates')}>
              {t('portal.update.backToUpdates')}
            </Button>
          }
        />
      </section>
    );
  }

  const projectName =
    PROJECTS.find((project) => project.id === publication.projectId)?.name ??
    t('portal.update.developmentFallback');

  /*
     Evidence is scoped to the period the update covers where the period names a
     month. Where it does not — or where nothing was released in that month — the
     development's published record is shown instead, and the panel says which of
     the two it is rather than implying one.
  */
  const allEvidence = photos.filter((item) => item.projectId === publication.projectId);
  const prefix = publication.publishedAt?.slice(0, 7) ?? null;
  const periodEvidence = prefix ? allEvidence.filter((item) => item.date.startsWith(prefix)) : [];
  const periodScoped = periodEvidence.length > 0;
  const evidence = periodScoped ? periodEvidence : allEvidence;

  const documents = files.filter((entry) => entry.projectId === publication.projectId);

  return (
    <div className="flex flex-col gap-8">
      <div>
        <Link
          to="/portal/updates"
          className={cn(
            'inline-flex items-center gap-1.5 rounded-xs text-sm font-medium text-muted no-underline hover:text-heading',
            FOCUS_RING_LINK,
          )}
        >
          <IconArrowLeft size={14} aria-hidden="true" />
          {t('portal.update.backLink')}
        </Link>
      </div>

      <header className="flex flex-col gap-3">
        <p className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
          {projectName} · {publication.period}
        </p>
        <h1 className="max-w-4xl font-display text-3xl font-semibold uppercase tracking-title text-heading">
          {publication.title}
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="success" variant="subtle" size="sm">
            {t('portal.update.publishedBadge', { date: formatDate(publication.publishedAt) })}
          </Badge>
          <Badge tone="neutral" variant="subtle" size="sm">
            {t('portal.update.periodBadge', { period: publication.period })}
          </Badge>
        </div>
      </header>

      {/* Provenance sits above the narrative, not under it. */}
      <Panel
        variant="elevated"
        className="border-l-4 border-l-primary"
        title={t('portal.update.approvalTitle')}
      >
        <dl className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
          <div>
            <dt className="text-2xs uppercase tracking-label text-muted">
              {t('portal.update.preparedBy')}
            </dt>
            <dd className="mt-1 text-sm text-body">{publication.preparedBy}</dd>
          </div>
          <div>
            <dt className="text-2xs uppercase tracking-label text-muted">
              {t('portal.update.reviewedBy')}
            </dt>
            <dd className="mt-1 text-sm text-body">{publication.reviewedBy ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-2xs uppercase tracking-label text-muted">
              {t('portal.update.approvedBy')}
            </dt>
            <dd className="mt-1 text-sm text-body">{publication.approvedBy ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-2xs uppercase tracking-label text-muted">
              {t('portal.update.approvedOn')}
            </dt>
            <dd className="mt-1 text-sm text-body">{formatDate(publication.approvedAt)}</dd>
          </div>
        </dl>

        <div className="mt-5 flex items-start gap-3 rounded-xs border border-line-subtle bg-surface-muted p-4">
          <IconShieldCheck size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-primary" />
          <p className="text-sm text-body">
            {t('portal.update.recordNote', {
              date: formatDate(publication.publishedAt),
            })}
          </p>
        </div>
      </Panel>

      <Panel title={t('portal.update.periodTitle')} description={publication.period}>
        <p className="max-w-3xl text-sm leading-relaxed text-body">{publication.body}</p>
      </Panel>

      <Panel
        title={t('portal.update.figuresTitle')}
        description={t('portal.update.figuresDescription')}
      >
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          <Metric
            label={t('portal.update.spendToDate')}
            value={publication.spendToDate}
            format="money"
            hint={t('portal.update.spendToDateHint')}
          />
          <Metric
            label={t('portal.update.againstForecast')}
            value={publication.variance}
            format="money"
            tone={publication.variance < 0 ? 'success' : 'warning'}
            hint={t('portal.update.againstForecastHint')}
          />
        </div>
      </Panel>

      <Panel
        title={t('portal.update.evidenceTitle')}
        description={
          periodScoped
            ? t('portal.update.evidencePeriod', { period: publication.period })
            : t('portal.update.evidenceRecord')
        }
      >
        {evidence.length === 0 ? (
          <p className="text-sm text-muted">{t('portal.update.noEvidence')}</p>
        ) : (
          <ul className="grid list-none gap-4 p-0 sm:grid-cols-2 xl:grid-cols-3">
            {evidence.map((item) => (
              <li
                key={item.id}
                className="flex flex-col gap-3 rounded-xs border border-line bg-surface p-4 shadow-hard-sm"
              >
                <div className="grid h-24 place-items-center rounded-xs bg-surface-muted">
                  <IconPhoto size={24} aria-hidden="true" className="text-muted" />
                </div>
                <p className="text-sm text-body">{item.caption}</p>
                <div className="mt-auto flex flex-wrap items-center gap-2">
                  <Badge tone="neutral" variant="subtle" size="sm">
                    {phaseLabel(item.phaseId)}
                  </Badge>
                  <span className="text-xs text-muted">{formatDate(item.date)}</span>
                </div>
                <p className="text-xs text-muted">
                  {t('portal.update.capturedBy', { name: item.capturedBy })}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel
        title={t('portal.update.documentsTitle')}
        description={t('portal.update.documentsDescription')}
        headerActions={
          <Button variant="outline" size="sm" onClick={() => navigate('/portal/documents')}>
            {t('portal.update.openDocuments')}
          </Button>
        }
      >
        {documents.length === 0 ? (
          <p className="text-sm text-muted">{t('portal.update.noDocuments')}</p>
        ) : (
          <ul className="list-none divide-y divide-line-subtle p-0">
            {documents.map((document) => (
              <li key={document.id} className="flex items-baseline justify-between gap-4 py-3 first:pt-0">
                <span className="min-w-0 text-sm text-body">{document.name}</span>
                <span className="shrink-0 text-xs text-muted">
                  {t(DOCUMENT_KIND[document.type])} · {formatDate(document.date)} ·{' '}
                  {document.size}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
